import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlAssemblyPartialModels, urlSaveAssembly } from '../../../Constants';
import { IAction, SliceProps, SlicePropsWithData } from '../../store';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectSaveAssemblyState = {
    data: null,
    loading: false,
    error: null,
    isSaveAssemblyDialogOpen: false,
    selectedDocument: null,
    linkedPartialModels: {
        data: null,
        loading: false,
        error: null,
    },
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openSaveAssemblyDialog(state: IProjectSaveAssemblyState, action: IAction<any>) {
        state.isSaveAssemblyDialogOpen = true;

        state.selectedDocument = action.payload;
    },
    closeSaveAssemblyDialog(state: IProjectSaveAssemblyState) {
        state.isSaveAssemblyDialogOpen = false;
    },
    resetErrorSaveAssembly(state: IProjectSaveAssemblyState) {
        state.error = null;
    },
    resetSaveAssembly(state: IProjectSaveAssemblyState) {
        state.data = null;
    },
    resetSelectedDocument(state: IProjectSaveAssemblyState) {
        state.selectedDocument = null;
    },
    clearLinkedPartialModels(state: IProjectSaveAssemblyState) {
        state.linkedPartialModels.data = null;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the documents contained within a project database view category.
     * @param config - Object that contains the project database's replicaId, the active view, and the category to load documents for.
     * @returns Promise for the HTTP request.
     */
    getLinkedPartialModels: async (config: {
        replicaId: string;
        idDocIndex?: string;
    }): Promise<ILinkedPartialModels[]> => {
        try {
            const { replicaId, idDocIndex } = config;

            const request: RequestInit = {
                method: 'GET',
                headers: {
                    replicaId,
                    idDocIndex: idDocIndex || '',
                },
            };

            const response = await apiFetch(urlAssemblyPartialModels, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that saves a an assembly with the linked partial models.
     * @param config - Object that contains the project database's replicaId, document subject, and linked model ids.
     * @returns Promise for the HTTP request.
     */
    saveAssemblyDocument: async (config: {
        replicaId: string;
        subject: string;
        linkedModelsIds: string[];
        assemblyId?: string;
    }): Promise<string> => {
        try {
            const { subject, replicaId, linkedModelsIds, assemblyId } = config;

            const formData = new FormData();
            formData.append('subject', subject || '');
            formData.append('linkedModelsIds', linkedModelsIds.toString());
            formData.append('assemblyId', assemblyId || '');

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlSaveAssembly, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectSaveAssemblyState>) => {
        /**
         * Reducers for the 'getLinkedPartialModels' thunk.
         */
        builder.addCase(
            getLinkedPartialModels.pending,
            (state: Draft<IProjectSaveAssemblyState>) => {
                state.linkedPartialModels.loading = true;
                state.linkedPartialModels.error = null;
            },
        );
        builder.addCase(
            getLinkedPartialModels.fulfilled,
            (state: Draft<IProjectSaveAssemblyState>, action: IAction<ILinkedPartialModels[]>) => {
                state.linkedPartialModels.loading = false;
                state.linkedPartialModels.error = null;
                state.linkedPartialModels.data = action.payload;
            },
        );
        builder.addCase(
            getLinkedPartialModels.rejected,
            (state: Draft<IProjectSaveAssemblyState>, action: IAction<SerializedError>) => {
                state.linkedPartialModels.loading = false;
                state.linkedPartialModels.error = action.error;
            },
        );

        /**
         * Reducers for the 'saveAssemblyDocument' thunk.
         */
        builder.addCase(saveAssemblyDocument.pending, (state: Draft<IProjectSaveAssemblyState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            saveAssemblyDocument.fulfilled,
            (state: Draft<IProjectSaveAssemblyState>, action: IAction<string>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            saveAssemblyDocument.rejected,
            (state: Draft<IProjectSaveAssemblyState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getLinkedPartialModels = createAsyncThunk(
    `${urlAssemblyPartialModels}`,
    thunks.getLinkedPartialModels,
);

export const saveAssemblyDocument = createAsyncThunk(
    `${urlSaveAssembly}`,
    thunks.saveAssemblyDocument,
);

export interface ILinkedPartialModels {
    /** Document IdDocIndex. */
    idDocIndex: string;

    /** If the document is linked to the given document. */
    isLinked: boolean;

    /** Subject input for the document. */
    subject: string;

    /** Document DocNumber. */
    docNumber: boolean;

    /** Document DocType. */
    docType: string;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectSaveAssemblyState extends SliceProps {
    /** Assembly document data. */
    data: string;

    /** Boolean value if the save assembly dialog is open. */
    isSaveAssemblyDialogOpen: boolean;

    /** Selected document. */
    selectedDocument: any;

    /** List of linked partial models. */
    linkedPartialModels: SlicePropsWithData<ILinkedPartialModels[]>;
}

/**
 * Create the redux slice.
 */
export const projectAssemblySlice = createSlice({
    name: 'projectAssemblySlice',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    openSaveAssemblyDialog,
    closeSaveAssemblyDialog,
    resetErrorSaveAssembly,
    resetSaveAssembly,
    resetSelectedDocument,
    clearLinkedPartialModels,
} = projectAssemblySlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectAssemblySlice.reducer;
